import { useMemo } from "react";
import { useWatch } from "react-hook-form";

import { useCurrentComposition, useCompositionForm } from "@design-lab/entities/composition";
import { useIsSample, useDesignLabConfig } from "@design-lab/shared/lib/config";
import type { Color } from "@design-lab/shared/types/base";

import { useFastestDeliveryStock } from "../api/useFastestDeliveryStock";

export function useProductColors({ maxColors }: { maxColors: number } = { maxColors: 30 }) {
  const { currentProduct } = useCurrentComposition();
  const { form } = useCompositionForm();
  const isSample = useIsSample();
  const config = useDesignLabConfig();
  const madeSamplesV1 = config.flip?.madeSamplesV1;

  const currentCompositionId = useWatch({
    name: "currentCompositionId",
    control: form.control
  });

  const addedCompositions = form.getValues("addedCompositions");
  const addedCompositionIds = useMemo(
    () => new Set((addedCompositions || []).map(comp => comp.id)),
    [addedCompositions]
  );

  // Fetch optimal DC stock data for the current product
  const catalogNumber = currentProduct?.catalogNumber;
  const { data: fastestDeliveryData, isLoading: isFastestDeliveryLoading } =
    useFastestDeliveryStock(catalogNumber);

  // Check if we have valid fastest delivery stock data
  const hasFastestDeliveryStockData = Boolean(
    fastestDeliveryData?.enabled &&
    fastestDeliveryData.variantTotalStockCounts &&
    Object.keys(fastestDeliveryData.variantTotalStockCounts).length > 0
  );

  // Build a helper to check if a composition has any stock
  const isCompositionOutOfStock = useMemo(() => {
    if (!hasFastestDeliveryStockData || !fastestDeliveryData?.variantTotalStockCounts) {
      return null; // Signal to use API-provided outOfStock
    }

    const stockCounts = fastestDeliveryData.variantTotalStockCounts;
    return (composition: { variants: { id: string }[]; outOfStock?: boolean }) => {
      // Check if any variant has stock
      const hasStock = composition.variants.some(variant => {
        const count = stockCounts[String(variant.id)];
        return count !== undefined && count > 0;
      });
      return !hasStock;
    };
  }, [hasFastestDeliveryStockData, fastestDeliveryData]);

  const colors: Color[] = useMemo(() => {
    const { compositions } = currentProduct || {};
    if (!compositions) return [];

    const filteredCompositions = compositions.filter(comp => {
      if (isSample && !madeSamplesV1) {
        return comp.availableForSample === true;
      }

      return true;
    });

    return filteredCompositions.map(comp => {
      // Use fastest delivery stock data to determine OOS if available,
      // otherwise fall back to API-provided outOfStock
      const outOfStock = isCompositionOutOfStock
        ? isCompositionOutOfStock(comp)
        : (comp.outOfStock ?? false);

      return {
        id: comp.id,
        name: comp.colorName,
        hex: comp.colorHex || "#000000",
        swatchUrl: comp.swatchUrl,
        materials: comp.materials,
        selected: false,
        isJiffy1st: comp.jiffy1st ?? false,
        outOfStock
      };
    });
  }, [currentProduct, isSample, madeSamplesV1, isCompositionOutOfStock]);

  const allColors = colors.map(color => ({
    ...color,
    selected: color.id === currentCompositionId
  }));

  // Use optimal DC composition IDs if available, otherwise fall back to jiffy1st
  const optimalCompositionIds = useMemo(() => {
    if (fastestDeliveryData?.enabled && fastestDeliveryData.availableCompositionIds.length > 0) {
      return new Set(fastestDeliveryData.availableCompositionIds.map(id => String(id)));
    }
    return null;
  }, [fastestDeliveryData]);

  const recommendedColors: Color[] = [];
  const otherColors: Color[] = [];

  allColors.forEach(color => {
    // Use optimal DC data if available, otherwise fall back to jiffy1st
    const isOptimalDC = optimalCompositionIds
      ? optimalCompositionIds.has(color.id)
      : color.isJiffy1st;

    if (isOptimalDC) {
      recommendedColors.push(color);
    } else {
      otherColors.push(color);
    }
  });

  // Sort colors: in-stock first, out-of-stock last (matching PDP behavior)
  const sortByStock = (a: Color, b: Color) => {
    if (a.outOfStock === b.outOfStock) return 0;
    return a.outOfStock ? 1 : -1;
  };

  recommendedColors.sort(sortByStock);
  otherColors.sort(sortByStock);

  const selectedColor = allColors.find(c => c.id === currentCompositionId);

  return {
    colors: otherColors,
    recommendedColors,
    selectedColor,
    addedCompositionIds,
    currentProduct,
    maxVisibleColors: maxColors,
    isShowAllAvailable: allColors.length > maxColors,
    isFastestDeliveryLoading
  };
}
